export class ElementDetector {
  constructor() {
    this.forms = new Map();
    this.lists = new Map(); // Will now store arrays of lists per name
    this.fields = new Map();
  }

  detectAll() {
    this.detectForms();
    this.detectLists();
    this.detectFields();
    
    return {
      forms: this.forms,
      lists: this.lists,
      fields: this.fields
    };
  }

  detectForms() {
    const forms = document.querySelectorAll('[pixl-search-form]');
    
    forms.forEach(form => {
      const formName = form.getAttribute('pixl-search-form');
      const inputs = form.querySelectorAll('[pixl-search-field]');
      
      this.forms.set(formName, {
        element: form,
        inputs: Array.from(inputs),
        name: formName
      });
    });
    
    return this.forms;
  }

  detectLists() {
    const lists = document.querySelectorAll('[pixl-search-list]');
    
    lists.forEach(list => {
      const listName = list.getAttribute('pixl-search-list');
      const highlightClass = list.getAttribute('pixl-highlight-class') || 'pixl-highlight';
      const highlightColor = list.getAttribute('pixl-highlight-color') || '#ffeb3b';
      const urlParams = list.getAttribute('pixl-url-params') === 'true';
      const analyticsKey = list.getAttribute('pixl-analytics-key');
      
      // Empty state customization attributes
      const emptyMessage = list.getAttribute('pixl-empty-message') || 'No results found in this category';
      const emptyColor = list.getAttribute('pixl-empty-color') || '#666666';
      const emptySize = list.getAttribute('pixl-empty-size') || '16px';
      const emptyPadding = list.getAttribute('pixl-empty-padding') || '40px 20px';
      const emptyAlign = list.getAttribute('pixl-empty-align') || 'left';
      const emptyWeight = list.getAttribute('pixl-empty-weight') || 'normal';
      const emptyClass = list.getAttribute('pixl-empty-class') || '';
      const emptyTarget = list.getAttribute('pixl-empty-target') || '';
      
      const items = this.findCollectionItems(list);
      
      const listData = {
        element: list,
        items: items,
        name: listName,
        highlightClass: highlightClass,
        highlightColor: highlightColor,
        urlParams: urlParams,
        analyticsKey: analyticsKey,
        emptyMessage: emptyMessage,
        emptyColor: emptyColor,
        emptySize: emptySize,
        emptyPadding: emptyPadding,
        emptyAlign: emptyAlign,
        emptyWeight: emptyWeight,
        emptyClass: emptyClass,
        emptyTarget: emptyTarget
      };
      
      // Store multiple lists with the same name in an array
      if (!this.lists.has(listName)) {
        this.lists.set(listName, []);
      }
      this.lists.get(listName).push(listData);
    });
    
    return this.lists;
  }

  detectFields() {
    const fields = document.querySelectorAll('[pixl-search-field]');
    
    fields.forEach(field => {
      const fieldName = field.getAttribute('pixl-search-field');
      
      if (!this.fields.has(fieldName)) {
        this.fields.set(fieldName, []);
      }
      
      this.fields.get(fieldName).push(field);
    });
    
    return this.fields;
  }

  findCollectionItems(listElement) {
    let items = listElement.querySelectorAll('[pixl-search-item]');
    
    if (items.length === 0) {
      items = listElement.querySelectorAll('.w-dyn-item');
    }
    
    if (items.length === 0) {
      items = listElement.querySelectorAll('[role="listitem"]');
    }
    
    if (items.length === 0) {
      const firstChild = listElement.firstElementChild;
      if (firstChild && firstChild.children.length > 1) {
        items = firstChild.children;
      }
    }
    
    return Array.from(items);
  }

  linkFormsToLists() {
    const connections = [];
    
    this.forms.forEach((formData, formName) => {
      const matchingLists = this.findMatchingLists(formName);
      
      if (matchingLists.length > 0) {
        // Create one connection with multiple lists
        connections.push({
          form: formData,
          lists: matchingLists, // Changed from single list to array of lists
          list: matchingLists[0], // Keep for backward compatibility
          fields: this.getFieldsForLists(matchingLists) // Get fields from all lists
        });
      }
    });
    
    return connections;
  }

  findMatchingList(formName) {
    for (const [listName, listsArray] of this.lists) {
      if (listName === formName || formName === 'filters') {
        // Return the first list for backward compatibility
        return listsArray[0];
      }
    }
    return null;
  }

  findMatchingLists(formName) {
    const matchingLists = [];
    
    for (const [listName, listsArray] of this.lists) {
      // Match if list name equals form name, or if form is generic "filters"
      if (listName === formName || formName === 'filters') {
        // Add all lists with this name
        matchingLists.push(...listsArray);
      }
    }
    
    return matchingLists;
  }

  getFieldsForList(listData) {
    const fields = new Map();
    
    listData.items.forEach(item => {
      const itemFields = item.querySelectorAll('[pixl-search-field]');
      
      itemFields.forEach(field => {
        const fieldName = field.getAttribute('pixl-search-field');
        
        if (!fields.has(fieldName)) {
          fields.set(fieldName, []);
        }
        
        fields.get(fieldName).push({
          element: field,
          item: item
        });
      });
    });
    
    return fields;
  }

  getFieldsForLists(listsArray) {
    const fields = new Map();
    
    // Combine fields from all lists
    listsArray.forEach(listData => {
      listData.items.forEach(item => {
        const itemFields = item.querySelectorAll('[pixl-search-field]');
        
        itemFields.forEach(field => {
          const fieldName = field.getAttribute('pixl-search-field');
          
          if (!fields.has(fieldName)) {
            fields.set(fieldName, []);
          }
          
          fields.get(fieldName).push({
            element: field,
            item: item,
            list: listData // Track which list this field belongs to
          });
        });
      });
    });
    
    return fields;
  }

  refresh() {
    this.forms.clear();
    this.lists.clear();
    this.fields.clear();
    return this.detectAll();
  }
}